/* eslint-disable no-empty */
import { IUser } from '@sdk/contracts';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';

import { initSocketActions, ISocketActions, useSocketState } from '@/hooks';

const SOCKET_URL = import.meta.env['VITE_SOCKET_URL'];
const MAX_PENDING_QUEUE_SIZE = 100;
const MAX_PENDING_ITEM_RETRIES = 3;

type AuthUser = Pick<IUser, '_id' | 'name' | 'lastName' | 'role' | 'academy'>;
type PendingItem = { event: string; payload: unknown; retries?: number };
type SocketHandler<T extends unknown[] = unknown[]> = (...args: T) => void;

export const SocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const setConnected = useSocketState(state => state.setConnected);
  const [ready, setReady] = useState(false);

  const socketRef = useRef<Socket | null>(null);

  const pendingQueueRef = useRef<PendingItem[]>([]);
  const lastAuthUserRef = useRef<AuthUser | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const isReattachingRef = useRef(false);
  const listenersRegistryRef = useRef(new Map<string, Set<SocketHandler<unknown[]>>>());
  const onceListenersRegistryRef = useRef(new Map<string, Set<SocketHandler<unknown[]>>>());
  const onceWrapperMapRef = useRef(
    new Map<string, Map<SocketHandler<unknown[]>, (...args: unknown[]) => void>>()
  );

  useEffect(() => {
    socketRef.current = io(SOCKET_URL, {
      autoConnect: false,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000
    });

    const onSocketConnected = () => {
      setConnected(true);

      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const pendingQueue = pendingQueueRef.current;
      while (pendingQueue.length > 0) {
        const item = pendingQueue.shift();
        if (item) {
          try {
            socketRef.current?.emit(item.event, item.payload);
          } catch (error) {
            const retries = (item.retries || 0) + 1;
            if (retries < MAX_PENDING_ITEM_RETRIES) {
              pendingQueue.unshift({ ...item, retries });
              break;
            }
          }
        }
      }
    };

    const onSocketDisconnected = () => {
      setConnected(false);

      onceWrapperMapRef.current.forEach((mapForEvent, event) => {
        mapForEvent.forEach(wrapped => {
          try {
            socketRef.current?.off(event, wrapped);
          } catch {}
        });
      });
      onceWrapperMapRef.current.clear();
    };

    const attachCoreHandlers = () => {
      if (!socketRef.current) return;
      socketRef.current.off('connect', onSocketConnected);
      socketRef.current.off('disconnect', onSocketDisconnected);
      socketRef.current.on('connect', onSocketConnected);
      socketRef.current.on('disconnect', onSocketDisconnected);
    };

    const detachAllRegisteredListeners = () => {
      if (!socketRef.current) return;

      listenersRegistryRef.current.forEach((handlers, event) => {
        handlers.forEach(h => {
          socketRef.current?.off(event, h as (...args: unknown[]) => void);
        });
      });

      onceWrapperMapRef.current.forEach((mapForEvent, event) => {
        mapForEvent.forEach(wrapped => {
          socketRef.current?.off(event, wrapped);
        });
      });
    };

    const reattachRegisteredListeners = () => {
      if (!socketRef.current) return;
      if (isReattachingRef.current) return;

      isReattachingRef.current = true;

      try {
        detachAllRegisteredListeners();

        listenersRegistryRef.current.forEach((handlers, event) => {
          handlers.forEach(h => {
            socketRef.current?.on(event, h as (...args: unknown[]) => void);
          });
        });

        onceListenersRegistryRef.current.forEach((handlers, event) => {
          let mapForEvent = onceWrapperMapRef.current.get(event);
          if (!mapForEvent) {
            mapForEvent = new Map();
            onceWrapperMapRef.current.set(event, mapForEvent);
          }

          const handlersToRemove: SocketHandler<unknown[]>[] = [];
          mapForEvent.forEach((wrapped, handler) => {
            if (!handlers.has(handler)) {
              handlersToRemove.push(handler);
            }
          });
          handlersToRemove.forEach(h => mapForEvent!.delete(h));

          handlers.forEach(h => {
            if (mapForEvent!.has(h as SocketHandler<unknown[]>)) return;
            const wrapped = (...args: unknown[]) => {
              (h as (...a: unknown[]) => void)(...args);
              mapForEvent!.delete(h as SocketHandler<unknown[]>);
              onceListenersRegistryRef.current.get(event)?.delete(h as SocketHandler<unknown[]>);
            };
            mapForEvent!.set(h as SocketHandler<unknown[]>, wrapped);
            socketRef.current?.once(event, wrapped);
          });
        });

        onceWrapperMapRef.current.forEach((mapForEvent, event) => {
          if (!onceListenersRegistryRef.current.has(event)) {
            onceWrapperMapRef.current.delete(event);
          }
        });
      } finally {
        isReattachingRef.current = false;
      }
    };

    const buildSocket = () => {
      if (!socketRef.current) return;
      try {
        socketRef.current.off?.('connect');
        socketRef.current.off?.('disconnect');
        socketRef.current.removeAllListeners?.();
        socketRef.current.disconnect?.();

        if (lastAuthUserRef.current) {
          socketRef.current.auth = lastAuthUserRef.current;
        }

        attachCoreHandlers();
        reattachRegisteredListeners();
        socketRef.current.connect();
      } catch (error) {}
    };

    const ensureConnected = () => {
      if (!socketRef.current) return;
      if (socketRef.current.connected) return;
      if (!lastAuthUserRef.current) return;

      if (reconnectTimeoutRef.current) {
        return;
      }

      try {
        socketRef.current.auth = lastAuthUserRef.current;
        attachCoreHandlers();
        reattachRegisteredListeners();
        socketRef.current.connect();

        const timeoutId = window.setTimeout(() => {
          if (
            socketRef.current &&
            !socketRef.current.connected &&
            reconnectTimeoutRef.current === timeoutId
          ) {
            buildSocket();
          }
          reconnectTimeoutRef.current = null;
        }, 5000);

        reconnectTimeoutRef.current = timeoutId;
      } catch (error) {}
    };

    const socketActions: ISocketActions = {
      connect: (user: IUser) => {
        if (!socketRef.current || socketRef.current.connected) {
          return undefined;
        }

        try {
          const { _id, name, lastName, role, academy } = user;
          const authData = { _id, name, lastName, role, academy };
          socketRef.current.auth = authData;
          lastAuthUserRef.current = authData;

          attachCoreHandlers();
          reattachRegisteredListeners();
          socketRef.current.connect();

          return () => {
            socketRef.current?.disconnect();
            setConnected(false);
            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
          };
        } catch (error) {
          return undefined;
        }
      },

      emitSafe: (event: string, payload: unknown) => {
        if (socketRef.current?.connected) {
          try {
            socketRef.current.emit(event, payload);
            return;
          } catch (error) {}
        }

        const pendingQueue = pendingQueueRef.current;
        if (pendingQueue.length >= MAX_PENDING_QUEUE_SIZE) {
          pendingQueue.shift();
        }

        pendingQueue.push({ event, payload });
        ensureConnected();
      },

      on: <T extends unknown[] = unknown[]>(event: string, handler: SocketHandler<T>) => {
        const listenersRegistry = listenersRegistryRef.current;
        let setForEvent = listenersRegistry.get(event);
        if (!setForEvent) {
          setForEvent = new Set<SocketHandler<unknown[]>>();
          listenersRegistry.set(event, setForEvent);
        }
        if (setForEvent.has(handler as SocketHandler<unknown[]>)) return;
        setForEvent.add(handler as SocketHandler<unknown[]>);
        socketRef.current?.on(event, handler as (...args: unknown[]) => void);
      },

      off: <T extends unknown[] = unknown[]>(event: string, handler?: SocketHandler<T>) => {
        const listenersRegistry = listenersRegistryRef.current;
        const onceListenersRegistry = onceListenersRegistryRef.current;
        const onceWrapperMap = onceWrapperMapRef.current;

        if (handler) {
          listenersRegistry.get(event)?.delete(handler as SocketHandler<unknown[]>);
          onceListenersRegistry.get(event)?.delete(handler as SocketHandler<unknown[]>);

          const wrapped = onceWrapperMap.get(event)?.get(handler as SocketHandler<unknown[]>);
          if (wrapped) {
            onceWrapperMap.get(event)?.delete(handler as SocketHandler<unknown[]>);
            socketRef.current?.off(event, wrapped);
          } else {
            socketRef.current?.off(event, handler as (...args: unknown[]) => void);
          }
          return;
        }

        listenersRegistry.delete(event);
        onceListenersRegistry.delete(event);
        onceWrapperMap.delete(event);
        socketRef.current?.off(event);
      },

      once: <T extends unknown[] = unknown[]>(event: string, handler: SocketHandler<T>) => {
        const onceListenersRegistry = onceListenersRegistryRef.current;
        let setForEvent = onceListenersRegistry.get(event);
        if (!setForEvent) {
          setForEvent = new Set<SocketHandler<unknown[]>>();
          onceListenersRegistry.set(event, setForEvent);
        }
        setForEvent.add(handler as SocketHandler<unknown[]>);

        let mapForEvent = onceWrapperMapRef.current.get(event);
        if (!mapForEvent) {
          mapForEvent = new Map();
          onceWrapperMapRef.current.set(event, mapForEvent);
        }

        const wrappedHandler = (...args: unknown[]) => {
          handler(...(args as T));
          mapForEvent!.delete(handler as SocketHandler<unknown[]>);
          onceListenersRegistry.get(event)?.delete(handler as SocketHandler<unknown[]>);
        };

        mapForEvent.set(handler as SocketHandler<unknown[]>, wrappedHandler);
        socketRef.current?.once(event, wrappedHandler);
      },

      emit: (event: string, ...args: unknown[]) => {
        try {
          socketRef.current?.emit(event, ...args);
        } catch (error) {}
      },

      hasListeners: (event: string) => {
        const regularListeners = listenersRegistryRef.current.get(event)?.size ?? 0;
        const onceListeners = onceListenersRegistryRef.current.get(event)?.size ?? 0;
        return regularListeners + onceListeners > 0;
      }
    };

    initSocketActions(socketActions);
    setReady(true);

    return () => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      socketRef.current?.disconnect();
      socketRef.current?.removeAllListeners();
    };
  }, [setConnected]);

  if (!ready) return null;

  return <>{children}</>;
};
